import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Max height as vh, default 70 */
  maxHeight?: number;
  /** Title for the sheet header (optional) */
  title?: string;
}

export function BottomSheet({ 
  isOpen, 
  onClose, 
  children, 
  maxHeight = 70,
  title 
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Touch handlers for swipe-to-dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    const startY = e.touches[0]?.clientY;
    if (startY !== undefined) {
      setTouchStart(startY);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === null || touchStart === undefined) return;
    
    const currentY = e.touches[0]?.clientY;
    if (currentY === undefined) return;
    
    const delta = currentY - touchStart;
    
    // Only allow dragging down
    if (delta > 0) {
      setTouchDelta(delta);
    }
  };

  const handleTouchEnd = () => {
    if (touchDelta > 100) {
      onClose();
    }
    setTouchStart(null);
    setTouchDelta(0);
    setIsDragging(false);
  };

  if (typeof window === 'undefined') return null;

  const sheetStyle: React.CSSProperties = {
    maxHeight: `${maxHeight}vh`,
    transform: isDragging && touchDelta > 0 
      ? `translateY(${touchDelta}px)` 
      : isOpen 
        ? 'translateY(0)' 
        : 'translateY(100%)',
    transition: isDragging ? 'none' : 'transform 300ms ease-out',
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Sheet */}
      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 z-50 bg-bg-surface rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)]"
        style={sheetStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-modal="true"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-8 h-1 rounded-full bg-text-muted/30" />
        </div>
        
        {/* Optional title */}
        {title && (
          <div className="px-4 pb-2 font-semibold text-text-primary">
            {title}
          </div>
        )}
        
        {/* Scrollable content */}
        <div 
          className="overflow-y-auto"
          style={{ maxHeight: `calc(${maxHeight}vh - 48px)` }}
        >
          {children}
        </div>
      </div>
    </>,
    document.body
  );
}
